/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {getMaxValueInArray} from '@ion/core/src/utils/ArrayUtil';
import {isValidURL} from '@ion/core/src/utils/Validators';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {SSOAppEntry} from './SSOAppEntry';
import {appNameExists, SSOFormFields, validateSSOApps} from './SSOEditorUtil';

export interface Entry {
  id: number;
  apps: GenericObject;
};

export interface UsersProps {
  onChange: (apps: GenericObject) => void;
  ssoAppInfo: Entry[];
  minApp?: number;
  bottomBorder?: boolean;
  required?: boolean;
  disabled?: boolean;
};

const useStyles = makeStyles(theme => ({
  container : {
    margin : '0px'
  },
  buttonStyle : {
    display        : 'flex',
    justifyContent : 'flex-end',
    width          : '100%',
    paddingTop     : '10px'
  },
  deleteStyle : {
    top : '25px'
  }
}));

/**
 * The SSO Apps Editor provides the UI which allows to add new apps, such as their
 * appId, clientId, clientSecret and redirectUri on new SSO Configuration creation.
 *
 * ```typescript
 *
 *    import {SSOApps} from '@ion/applications/src/pages/admin/accounts/SSOApps';
 *
 *    <SSOApps onChange={onChange}
 *      ssoAppInfo={ssoAppInfo}
 *      minApp={1} />
 *
 * ```
 */
export const SSOApps = ({
  onChange,
  ssoAppInfo,
  minApp = 0,
  disabled = false,
  bottomBorder = false
}: UsersProps) => {
  const classes                 = useStyles();
  const [ssoLevel, setSSOLevel] = useState<Entry[]>(cloneDeep(ssoAppInfo) || []);
  const appOptions              = translate('admin-groups:appListOptions', {returnObjects: true});

  useEffect(() => {
    if (minApp > 0 && isEmpty(ssoAppInfo)) {
      addNewEntry();
    }
  }, []);

  useEffect(() => {
    onChange?.(ssoLevel);
  }, [ssoLevel]);

  const onDelete = useCallback((id: string) => (e: MouseEvent<HTMLDivElement>) => {
    if (ssoLevel?.length > minApp) {
      const identifier = Number(id);
      setSSOLevel(ssoLevel?.filter((apps) => Number(apps.id) !== identifier));
    }
  }, [ssoLevel, minApp]);

  const addNewEntry = useCallback(() => {
    const {isValid, appList} = validateSSOApps(ssoLevel);
    if (isValid) {
      const allIds = ssoLevel?.map(({id}) => id);
      const maxId = ssoLevel?.length ? getMaxValueInArray(allIds) : 0;
      const newEntry: Entry = {
        id   : maxId + 1,
        apps : {
          [SSOFormFields.AppId]                   : ssoLevel?.length ? '' : appOptions[0]?.value,
          [SSOFormFields.ClientId]                : '',
          [SSOFormFields.ClientSecret]            : '',
          [SSOFormFields.RedirectUri]             : '',
          [`${SSOFormFields.AppId}_error`]        : false,
          [`${SSOFormFields.ClientId}_error`]     : false,
          [`${SSOFormFields.ClientSecret}_error`] : false,
          [`${SSOFormFields.RedirectUri}_error`]  : false
        }
      };
      setSSOLevel([...ssoLevel, newEntry]);
    } else {
      setSSOLevel(appList);
    }
  }, [ssoLevel]);

  const updateEntry = useCallback(function(id: string, name: string, value: string): void {
    const identifier = Number(id.slice(-1));
    const errorEntity = `${name}_error`;
    const updatedData = ssoLevel?.map((entry) => {
      if (Number(entry.id) === identifier) {
        entry.apps[name] = value;
        if (name === SSOFormFields.AppId && value !== '') {
          entry.apps[errorEntity] = appNameExists(ssoLevel);
        } else if (name === SSOFormFields.ClientId && value === '') {
          entry.apps[errorEntity] = true;
        } else if (name === SSOFormFields.RedirectUri && value !== '') {
          entry.apps[errorEntity] = !isValidURL(value);
        } else {
          entry.apps[errorEntity] = false;
        }
      }
      return entry;
    });
    setSSOLevel(cloneDeep(updatedData));
  }, [ssoLevel]);

  const handleFieldChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist?.();
    const {name, value, id} = e.target;
    updateEntry(id, name, value);
  }, [ssoLevel]);

  const handleChooserChange = useCallback(({name, value, id}) => {
    updateEntry(id, name, value);
  }, [ssoLevel]);

  return (
    <div className={classes.container}>
      {ssoLevel?.length < 2 && (
        <div className={classes.buttonStyle}>
          <TertiaryButton disabled={disabled} onClick={addNewEntry}>
            {translate('common:add')}
          </TertiaryButton>
        </div>
      )}
      <FormSection bottomBorder={bottomBorder} title=''>
        {ssoLevel?.map(item => (
          <SSOAppEntry handleChooserChange={handleChooserChange}
            key={`app-${item.id}`}
            disabled={disabled}
            handleFieldChange={handleFieldChange}
            onDelete={onDelete}
            classes={classes}
            ssoLevel={ssoLevel}
            item={item}
            minApp={minApp} />
        ))}
      </FormSection>
    </div>
  );
};

export default SSOApps;
